import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Card } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { FieldError } from '../../components/ui/FieldError';
import { Alert } from '../../components/ui/Alert';
import { isValidCnpj, maskCnpj, normalizeCnpj } from '../../utils/cnpj';
import { lookupCnpj } from '../onboarding/brasilapi';
import { createOrg, selectOrg, ApiProblemError } from '../../lib/api-client';
import { useAuth } from '../onboarding/AuthContext';

/**
 * `/orgs/new` (P3 — "agência/contador multi-CNPJ", PRD persona P3; R1's
 * "usuário pode pertencer a N orgs"). Gap found by the user 2026-08-27: the
 * backend already lets any authenticated user create additional orgs
 * (`orgs.routes.ts#createOrgHandler` has no "already has one" restriction —
 * confirmed before writing this page), but nothing in the dashboard, past
 * the very first signup, ever called `POST /internal/orgs` again — a
 * lojista/agência had no way to add a second CNPJ once onboarded.
 *
 * Deliberately NOT reusing `CnpjStep.tsx` as a shared component: that
 * screen is wired into the onboarding step machine (`useOnboardingState`,
 * `ensureKeys` before navigating to `/onboarding/providers`) — a second+
 * org for an already-onboarded user skips all of that and lands on
 * `/providers` directly, since `OrgSwitcher`'s existing claim-refresh
 * plumbing already makes every session-keyed context (session API key,
 * labels, etc.) re-derive itself once `orgId` changes, exactly like
 * switching to an existing org does.
 */
export function AddOrgPage() {
  const cnpjId = useId();
  const legalNameId = useId();
  const tradeNameId = useId();

  const { user, refreshOrgClaim } = useAuth();
  const navigate = useNavigate();

  const [cnpjInput, setCnpjInput] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const [cnpjError, setCnpjError] = useState<string | undefined>(undefined);
  const [legalNameError, setLegalNameError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const lastLookedUpRef = useRef<string | null>(null);

  const digits = normalizeCnpj(cnpjInput);

  useEffect(() => {
    if (digits.length !== 14 || !isValidCnpj(digits) || lastLookedUpRef.current === digits) {
      return;
    }
    lastLookedUpRef.current = digits;
    setLookupLoading(true);
    setLookupFailed(false);
    lookupCnpj(digits)
      .then((result) => {
        if (result) {
          setLegalName((prev) => prev || result.legal_name);
          setTradeName((prev) => prev || result.trade_name || '');
        } else {
          setLookupFailed(true);
        }
      })
      .finally(() => setLookupLoading(false));
  }, [digits]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setCnpjError(undefined);
    setLegalNameError(undefined);

    if (digits.length !== 14 || !isValidCnpj(digits)) {
      setCnpjError('CNPJ inválido — confira os dígitos.');
      return;
    }
    if (legalName.trim().length === 0) {
      setLegalNameError('Informe a razão social.');
      return;
    }
    if (!user) {
      return;
    }

    setSubmitting(true);
    try {
      const idToken = await user.getIdToken();
      const trimmedTradeName = tradeName.trim();
      const org = await createOrg(idToken, {
        cnpj: digits,
        legal_name: legalName.trim(),
        ...(trimmedTradeName ? { trade_name: trimmedTradeName } : {}),
      });

      // Same select + claim-refresh sequence as `OrgSwitcher.tsx` — every
      // session-keyed context downstream (session API key, labels,
      // tracking, ...) re-derives itself from the new `orgId` claim on its
      // own, no separate cache-invalidation call needed here.
      await selectOrg(idToken, org.id);
      await refreshOrgClaim();

      navigate('/providers');
    } catch (err) {
      if (err instanceof ApiProblemError) {
        const fieldMessage = err.fieldError('cnpj');
        if (fieldMessage || err.status === 409) {
          setCnpjError(fieldMessage ?? 'Já existe uma organização cadastrada para este CNPJ.');
        } else {
          setFormError(err.problem.detail ?? err.problem.title);
        }
      } else {
        setFormError('Não foi possível criar a organização. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-medium text-foreground">Adicionar empresa</h1>
        <p className="text-sm text-muted-foreground">
          Cadastre outro CNPJ para gerenciar como uma organização separada — útil para agências/contadores que
          administram mais de um lojista. Você poderá alternar entre organizações a qualquer momento.
        </p>
      </div>

      <Card className="flex max-w-lg flex-col gap-4 p-6">
        {formError ? (
          <Alert variant="error" title="Não foi possível continuar">
            {formError}
          </Alert>
        ) : null}

        <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor={cnpjId}>CNPJ</Label>
            <Input
              id={cnpjId}
              inputMode="numeric"
              autoComplete="off"
              placeholder="00.000.000/0000-00"
              value={cnpjInput}
              invalid={Boolean(cnpjError)}
              aria-describedby={cnpjError ? `${cnpjId}-error` : undefined}
              onChange={(event) => setCnpjInput(maskCnpj(event.target.value))}
              required
            />
            <FieldError id={`${cnpjId}-error`} message={cnpjError} />
            {lookupLoading ? <p className="text-sm text-muted-foreground">Consultando CNPJ…</p> : null}
            {lookupFailed ? (
              <p className="text-sm text-muted-foreground">
                Não encontramos esse CNPJ na Receita — preencha os dados manualmente.
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={legalNameId}>Razão social</Label>
            <Input
              id={legalNameId}
              value={legalName}
              invalid={Boolean(legalNameError)}
              aria-describedby={legalNameError ? `${legalNameId}-error` : undefined}
              onChange={(event) => setLegalName(event.target.value)}
              required
            />
            <FieldError id={`${legalNameId}-error`} message={legalNameError} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={tradeNameId}>Nome fantasia (opcional)</Label>
            <Input id={tradeNameId} value={tradeName} onChange={(event) => setTradeName(event.target.value)} />
          </div>

          <div className="flex gap-3">
            <Button type="submit" variant="default" disabled={submitting} className="self-start">
              {submitting ? 'Criando organização…' : 'Criar organização'}
            </Button>
            <Button type="button" variant="outline" disabled={submitting} className="self-start" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
